import * as React from "react";
import { Box, Container, Stack, Typography, Chip, Paper, Divider } from "@mui/material";

import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import BathtubOutlinedIcon from "@mui/icons-material/BathtubOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";

import PropertyGallery from "./PropertyGallery";
import GooglePropertyMap from "../maps/GooglePropertyMap";
import NearbyPlaces from "./NearbyPlaces";
import AgentsSection from "../agents/AgentsSection";
import type { Agent } from "../agents/AgentCard";
import { useI18n } from "../../i18n/I18nProvider";
import BookingInquiryCard from "./BookingInquiryCard";
import GoogleMapsLoader from "../maps/GoogleMapsLoader";
import ShareActions from "./ShareActions";

function absUrl(u?: string | null) {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
  return base ? `${base}${u.startsWith("/") ? "" : "/"}${u}` : u;
}

function getPersonsValue(p: any) {
  // 1) new columns
  const pmin = p?.persons_min != null ? Number(p.persons_min) : null;
  const pmax = p?.persons_max != null ? Number(p.persons_max) : null;

  if (Number.isFinite(pmin) && Number.isFinite(pmax)) {
    return pmin === pmax ? String(pmax) : `${pmin}-${pmax}`;
  }
  if (Number.isFinite(pmax)) return String(pmax);
  if (Number.isFinite(pmin)) return String(pmin);

  // 2) fallback old extras: title=Persons
  const extras = p?.extras;
  const x = Array.isArray(extras)
    ? extras.find((e) => String(e.title).toLowerCase() === "persons")
    : null;

  return x?.value ?? null;
}


function FormGrid({ children, gap = 4 }: { children: React.ReactNode; gap?: number }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap,
        gridTemplateColumns: { xs: "1fr", md: "repeat(12, 1fr)" },
        alignItems: "start",
      }}
    >
      {children}
    </Box>
  );
}

function Col({ md, children }: { md: number; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        gridColumn: { xs: "1 / -1", md: `span ${md}` },
        minWidth: 0,
      }}
    >
      {children}
    </Box>
  );
}

export default function PropertyDetailsClient({ locale, p }: { locale: string; p: any }) {
  const { t } = useI18n();
  const displayTitle = p?.display_title || "";

  const isSale = String(p.listing_status || "").includes("sale");
  const badge = isSale ? t("badge_sale") : t("badge_rent");

  const persons = getPersonsValue(p);

  const priceText = p.price_on_call
    ? t("price_on_call")
    : p.price
      ? `${Number(p.price).toLocaleString(locale === "el" ? "el-GR" : "en-US")} ${p.price_currency ?? "EUR"}`
      : "";

  const hasCoords = p.latitude != null && p.longitude != null;
  const lat = hasCoords ? Number(p.latitude) : null;
  const lng = hasCoords ? Number(p.longitude) : null;

  const agents: Agent[] = [
    {
      name: "Eleftheria Ntaldou",
      roleKey: "agent_owner_role",
      phone: "306971753500",
      whatsapp: "306971753500",
      email: "ritadaldou@gmail.com",
      experienceYears: 15,
      photoSrc: "",
      areaLabel: "Halkidiki",
    },
    {
      name: "Vasilis",
      roleKey: "agent_manager_role",
      phone: "306987437622",
      whatsapp: "306987437622",
      email: "vkoustoudas@hotmail.com",
      experienceYears: 10,
      photoSrc: "",
      areaLabel: "Halkidiki",
    },
  ];

  const [href, setHref] = React.useState<string | undefined>(undefined);
  React.useEffect(() => setHref(window.location.href), []);

  // ✅ normalize images to absolute
  const featured = absUrl(p.featured_image_url);
  const images = Array.isArray(p.images)
    ? p.images.map((img: any) => ({
      ...img,
      image_url: absUrl(img.image_url),
    }))
    : [];

  return (
    <Box sx={{ bgcolor: "background.default", py: { xs: 3, md: 6 } }}>
      <Container maxWidth="lg">
        <Stack spacing={1.2} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip label={badge} />
            {p.label ? <Chip label={p.label} variant="outlined" /> : null}
            {priceText ? <Chip label={priceText} sx={{ ml: { md: "auto" }, fontWeight: 900 }} /> : null}
          </Stack>

          <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.6 }}>
            {displayTitle}
          </Typography>


          <Typography color="text.secondary">
            {[p.city, p.region, p.address].filter(Boolean).join(", ")}
          </Typography>
          <ShareActions title={displayTitle} />

        </Stack>

        <Box sx={{ mb: 4 }}>
          <PropertyGallery title={displayTitle} featured={featured} images={images} />
        </Box>

        <FormGrid gap={4}>
          <Col md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
                {p.bedrooms != null ? (
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <BedOutlinedIcon fontSize="small" />
                    <Typography fontWeight={800}>
                      {p.bedrooms} {t("beds_short")}
                    </Typography>
                  </Stack>
                ) : null}

                {p.bathrooms != null ? (
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <BathtubOutlinedIcon fontSize="small" />
                    <Typography fontWeight={800}>
                      {p.bathrooms} {t("baths_short")}
                    </Typography>
                  </Stack>
                ) : null}

                {p.size_m2 != null ? (
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <SquareFootOutlinedIcon fontSize="small" />
                    <Typography fontWeight={800}>{p.size_m2} m²</Typography>
                  </Stack>
                ) : null}

                {!isSale && persons ? (
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <GroupsOutlinedIcon fontSize="small" />
                    <Typography fontWeight={800}>
                      {persons} {t("persons_short")}
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {p.content ? (
                <Box
                  sx={{
                    "& img": { maxWidth: "100%", height: "auto" },
                    "& p": { mt: 0, mb: 1.4 },
                  }}
                  dangerouslySetInnerHTML={{ __html: p.content }}
                />
              ) : null}

              {Array.isArray(p.features) && p.features.length > 0 ? (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
                    {t("amenities_title")}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {p.features.map((f: string) => (
                      <Chip key={f} label={f} variant="outlined" />
                    ))}
                  </Stack>
                </>
              ) : null}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: { xs: 2, md: 3 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <AgentsSection agents={agents} t={t} />
            </Paper>
          </Col>
        </FormGrid>

        <Col md={4}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {t("quick_info_title") || "Quick info"}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.8 }}>
              {t("quick_info_hint") || "Use the map & booking section below to send an inquiry."}
            </Typography>
          </Paper>
        </Col>

        <Box sx={{ mt: 4 }}>
          <FormGrid gap={3}>
            <Col md={5}>
              <BookingInquiryCard
                propertyTitle={displayTitle}
                propertyId={p.id ?? null}
                propertySlug={p.slug ?? null}
                maxGuests={persons ? Number(persons) : null}
                locale={locale}
                propertyUrl={href}
              />
            </Col>



            <Col md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  height: "100%",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 900 }}>
                  {t("location_title")}
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  {t("location_subtitle")}
                </Typography>

                {!hasCoords ? (
                  <Typography color="text.secondary">{t("location_missing")}</Typography>
                ) : (
                  <>
                    <Box
                      sx={{
                        height: { xs: 280, md: 460 },
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                        mb: 2,
                      }}
                    >
                      <GooglePropertyMap lat={lat!} lng={lng!} />
                    </Box>

                    <GoogleMapsLoader>
                      <NearbyPlaces lat={lat} lng={lng} t={t} />
                    </GoogleMapsLoader>

                  </>
                )}
              </Paper>
            </Col>
          </FormGrid>
        </Box>
      </Container>
    </Box>
  );
}
